import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import type { EarlyChildhoodAgeBand } from '../curriculum/early-childhood-provenance';
import { buildSkillEvidence, SkillEvidenceSummary } from '../learning/learning-evidence';
import { ChildProfile, LearningAttempt, SkillProgress } from '../models/learning.models';

interface MetaRecord {
  key: string;
  value: string;
}

@Injectable({ providedIn: 'root' })
export class LocalLearningStore {
  private readonly database = this.openDatabase();

  async listProfiles(): Promise<ChildProfile[]> {
    const db = await this.database;
    const profiles = await this.request<ChildProfile[]>(
      db.transaction('profiles', 'readonly').objectStore('profiles').getAll()
    );
    return profiles.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  }

  async createProfile(
    nickname: string,
    age: number,
    earlyChildhoodAgeBand?: EarlyChildhoodAgeBand
  ): Promise<ChildProfile> {
    const profile: ChildProfile = {
      id: crypto.randomUUID(),
      nickname: nickname.trim(),
      age,
      earlyChildhoodAgeBand,
      createdAt: new Date().toISOString()
    };

    const db = await this.database;
    const transaction = db.transaction('profiles', 'readwrite');
    transaction.objectStore('profiles').put(profile);
    await this.complete(transaction);
    return profile;
  }

  async updateProfile(
    profileId: string,
    nickname: string,
    age: number,
    earlyChildhoodAgeBand?: EarlyChildhoodAgeBand
  ): Promise<ChildProfile> {
    const db = await this.database;
    const existing = await this.request<ChildProfile | undefined>(
      db.transaction('profiles', 'readonly').objectStore('profiles').get(profileId)
    );
    if (!existing) {
      throw new Error('Profile not found');
    }

    const updated: ChildProfile = {
      ...existing,
      nickname: nickname.trim(),
      age,
      earlyChildhoodAgeBand
    };
    const transaction = db.transaction('profiles', 'readwrite');
    transaction.objectStore('profiles').put(updated);
    await this.complete(transaction);
    return updated;
  }

  async deleteProfile(profileId: string): Promise<void> {
    const db = await this.database;
    const transaction = db.transaction(['profiles', 'attempts', 'meta'], 'readwrite');
    const completed = this.complete(transaction);
    const profiles = transaction.objectStore('profiles');
    const attempts = transaction.objectStore('attempts');
    const meta = transaction.objectStore('meta');

    profiles.delete(profileId);
    const activeProfileRequest = meta.get('activeProfileId');
    const deleteAttempts = this.deleteAttemptsForProfile(attempts.index('profileId'), profileId);

    const [activeProfile] = await Promise.all([
      this.request<MetaRecord | undefined>(activeProfileRequest),
      deleteAttempts
    ]);
    if (activeProfile?.value === profileId) {
      meta.delete('activeProfileId');
    }

    await completed;
  }

  async getActiveProfileId(): Promise<string | null> {
    const db = await this.database;
    const record = await this.request<MetaRecord | undefined>(
      db.transaction('meta', 'readonly').objectStore('meta').get('activeProfileId')
    );
    return record?.value ?? null;
  }

  async setActiveProfileId(profileId: string | null): Promise<void> {
    const db = await this.database;
    const transaction = db.transaction('meta', 'readwrite');
    const store = transaction.objectStore('meta');
    if (profileId) {
      store.put({ key: 'activeProfileId', value: profileId } satisfies MetaRecord);
    } else {
      store.delete('activeProfileId');
    }
    await this.complete(transaction);
  }

  async recordAttempt(attempt: Omit<LearningAttempt, 'id' | 'createdAt'>): Promise<void> {
    const db = await this.database;
    const transaction = db.transaction('attempts', 'readwrite');
    transaction.objectStore('attempts').put({
      ...attempt,
      id: crypto.randomUUID(),
      createdAt: new Date().toISOString()
    } satisfies LearningAttempt);
    await this.complete(transaction);
  }

  async listAttempts(): Promise<LearningAttempt[]> {
    const db = await this.database;
    return this.request<LearningAttempt[]>(
      db.transaction('attempts', 'readonly').objectStore('attempts').getAll()
    );
  }

  async getSkillEvidence(profileId: string, skillId: string): Promise<SkillEvidenceSummary> {
    const db = await this.database;
    const attempts = await this.request<LearningAttempt[]>(
      db.transaction('attempts', 'readonly').objectStore('attempts').index('profileId').getAll(profileId)
    );
    return buildSkillEvidence(attempts, profileId, skillId);
  }

  async getProgress(profileId: string, skillId: string): Promise<SkillProgress> {
    const evidence = await this.getSkillEvidence(profileId, skillId);
    return {
      attempts: evidence.attempts,
      correct: evidence.correct
    };
  }

  private deleteAttemptsForProfile(index: IDBIndex, profileId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = index.openCursor(IDBKeyRange.only(profileId));
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) {
          resolve();
          return;
        }
        cursor.delete();
        cursor.continue();
      };
      request.onerror = () => reject(request.error ?? new Error('Could not delete profile attempts'));
    });
  }

  private openDatabase(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(`${environment.storageNamespace}-db`, 1);

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains('profiles')) {
          db.createObjectStore('profiles', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('attempts')) {
          const attempts = db.createObjectStore('attempts', { keyPath: 'id' });
          attempts.createIndex('profileId', 'profileId', { unique: false });
        }
        if (!db.objectStoreNames.contains('meta')) {
          db.createObjectStore('meta', { keyPath: 'key' });
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('IndexedDB unavailable'));
    });
  }

  private request<T>(request: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
    });
  }

  private complete(transaction: IDBTransaction): Promise<void> {
    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error ?? new Error('IndexedDB transaction failed'));
      transaction.onabort = () => reject(transaction.error ?? new Error('IndexedDB transaction aborted'));
    });
  }
}
